import { useAccountBalances } from '@/src/api/payg';
import { ErrorBoundary, PageWithNavigation } from '@/src/components';
import { BalanceSummaryLoading } from '@/src/components/BalanceSummary/BalanceSummaryLoading';
import {
  SmartMeterBalanceCard,
  TradMeterBalanceCard,
} from '@/src/components/payg/BalanceCard';
import {
  BalanceInfoModal,
  useBalanceInfoModal,
} from '@/src/components/payg/BalanceInfoModal';
import { PaygNotifications } from '@/src/components/payg/PaygNotifications';
import {
  RemoteAlerts,
  useSupportBanner,
} from '@/src/components/payg/RemoteAlerts';
import { SmartMeterBookingCard } from '@/src/components/payg/SmartMeterBookingCard';
import { PAYG_EVENTS } from '@/src/constants/analytics';
import {
  PAYG_ACCOUNT_BALANCES_ERROR,
  USAGE_SUMMARY_WIDGET_ERROR,
} from '@/src/constants/error-section-ids';
import { paygTopupLink } from '@/src/core/linkGenerators';
import AnalyticsService from '@/src/services/analyticsService';
import { ErrorSectionId } from '@/src/types/ErrorSectionIds';
import { getErrorDetails } from '@/src/utils/errorUtils';
import { Col, Margin, Row } from '@ovotech/element-v5';
import { UsageSummary } from '@ovotech/energy-cx';
import { VisuallyHidden } from '@ovotech/nebula';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom-v5';
import { MajorIncidentGeneralBanner } from './components/mi-banners';
import { testIds } from './testIds';
import { useUsageWidgetVisibility as useUsageSummaryVisibility } from './useUsageWidgetVisibility';

type Cell = {
  key: string;
  errorBoundaryId: ErrorSectionId;
  content: React.JSX.Element;
};

export const PAYGHomePage = () => {
  const balancesQuery = useAccountBalances();
  const usageSummaryVisibility = useUsageSummaryVisibility();
  const { banner, disableTopups } = useSupportBanner();
  const balanceInfoModal = useBalanceInfoModal(
    !!balancesQuery.data?.balances.length,
  );

  const history = useHistory();

  useEffect(() => {
    AnalyticsService.dispatch(...PAYG_EVENTS.homeViewed());
  }, []);

  useEffect(() => {
    if (balancesQuery.error) {
      const { code, message } = getErrorDetails(balancesQuery.error);
      AnalyticsService.dispatch(
        ...PAYG_EVENTS.homeBalancesUnavailable({ code, message }),
      );
    }
  }, [balancesQuery.error]);

  const cells: Cell[] = (() => {
    const result: Cell[] = [];

    if (balancesQuery.isLoading) {
      result.push({
        key: 'balance-loading',
        errorBoundaryId: PAYG_ACCOUNT_BALANCES_ERROR,
        content: <BalanceSummaryLoading />,
      });
    } else {
      balancesQuery.data?.unavailableBalances
        .filter(meter => meter.reasons.includes('TRAD_METER'))
        .forEach(meter => {
          result.push({
            key: meter.msn,
            errorBoundaryId: PAYG_ACCOUNT_BALANCES_ERROR,
            content: <TradMeterBalanceCard {...meter} />,
          });
        });

      balancesQuery.data?.balances.forEach(meter => {
        result.push({
          key: meter.msn,
          errorBoundaryId: PAYG_ACCOUNT_BALANCES_ERROR,
          content: (
            <SmartMeterBalanceCard
              onClick={
                disableTopups
                  ? undefined
                  : () => history.push(paygTopupLink(meter.msn))
              }
              onInfoClick={balanceInfoModal.open}
              {...meter}
            />
          ),
        });
      });
    }

    if (usageSummaryVisibility.data?.showUsageWidget) {
      result.push({
        key: 'usage-summary',
        errorBoundaryId: USAGE_SUMMARY_WIDGET_ERROR,
        content: <UsageSummary />,
      });
    }

    return result;
  })();

  return (
    <PageWithNavigation title="Home">
      <VisuallyHidden>
        <h1 data-testid={testIds.payg.heading}>Home</h1>
      </VisuallyHidden>
      <MajorIncidentGeneralBanner />
      <Margin top={8} />
      {banner && <RemoteAlerts {...banner} />}
      <Margin top={8} />
      <PaygNotifications />
      <Margin bottom={8} />
      {cells.length > 0 && (
        <Row isNested>
          {cells.map(cell => (
            <Col key={cell.key} medium={6}>
              <ErrorBoundary errorId={cell.errorBoundaryId}>
                {cell.content}
              </ErrorBoundary>
            </Col>
          ))}
        </Row>
      )}
      <Margin bottom={8} />
      <SmartMeterBookingCard />
      {balanceInfoModal.isOpen && (
        <BalanceInfoModal onClose={balanceInfoModal.close} />
      )}
    </PageWithNavigation>
  );
};
